import type { Api } from './Api';
import type {
  BotStatus,
  BotSummary,
  Entitlement,
  IntelligenceMapTarget,
  IntelligenceRequest,
  ModuleKey,
  SupportMessage,
  UserProfile
} from '../domain/types';
import { defaultSettings } from '../domain/types';
import type { DlIntelligenceStatus, RegionSeedResult, ShieldWatchEvent } from '../domain/intelligence';
import {
  DL_INTEL_FREE_SEARCHES_PER_DAY,
  DL_INTEL_REGION_SEARCH_COST,
  DL_INTEL_SEARCH_COST,
  dlSearchCost,
  isValidMappingRegion
} from '../domain/intelligence';

// Adaptador de desarrollo: estado en memoria, sin Supabase. Permite construir y
// ver la UI completa sin infra. Para probar la PASARELA, en la consola:
//   localStorage.setItem('mock_entitlement','inactive')  -> pantalla Suscribirse
//   localStorage.setItem('mock_entitlement','active')    -> dashboard
const MOCK_USER: UserProfile = { id: 'mock-user', email: 'demo@whitebot.app' };

// Semilla de demo: una búsqueda completada con varios resultados, para poder
// ver/maquetar las tarjetas del panel (doc 13 §8) sin backend real. La misma
// lista sirve para simular el volcado de región completa (doc 13 §9).
const SEC_RESULTS: IntelligenceMapTarget[] = [
  { playerName: 'CrazyMan', allianceTag: 'SEC', allianceName: 'SecondHome', power: 30_076_322, castleX: 711, castleY: 761, lastSeenAt: new Date(Date.now() - 3600_000).toISOString() },
  { playerName: 'FrostBuzzer', allianceTag: 'SEC', allianceName: 'SecondHome', power: 12_801_828, castleX: 721, castleY: 756, lastSeenAt: new Date(Date.now() - 5 * 3600_000).toISOString() },
  { playerName: 'MODESTY', allianceTag: 'SEC', allianceName: 'SecondHome', power: 11_221_840, castleX: 725, castleY: 771, lastSeenAt: new Date(Date.now() - 9 * 24 * 3600_000).toISOString() },
  { playerName: 'Teni', allianceTag: 'SEC', allianceName: 'SecondHome', power: 9_321_405, castleX: 702, castleY: 770, lastSeenAt: new Date(Date.now() - 3600_000).toISOString() },
  { playerName: 'Atalanta', allianceTag: 'SEC', allianceName: 'SecondHome', power: 7_521_295, castleX: 713, castleY: 758, lastSeenAt: new Date(Date.now() - 46 * 24 * 3600_000).toISOString() }
];

const intelligenceRequests: IntelligenceRequest[] = [
  {
    id: 'mock-intel-seed',
    playerName: 'sec',
    regionId: '4347',
    status: 'completed',
    createdAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
    results: [...SEC_RESULTS]
  }
];

// Economía de tokens en memoria (0.6.4, doc 17): saldo + gratis del día para
// probar el chip, los costes y el error 'insufficient tokens' sin backend.
// En consola: localStorage.setItem('mock_intel_tokens','0') para forzar el CTA.
const intelEconomy = {
  balance: Number(localStorage.getItem('mock_intel_tokens') ?? '7'),
  freeLeft: DL_INTEL_FREE_SEARCHES_PER_DAY,
  earnedToday: 34,
  scannedToday: 31,
  cellsScanned: 8_912,
  cellsTotal: 24_986
};

function mockIntelStatus(regionId?: string): DlIntelligenceStatus {
  return {
    balance: intelEconomy.balance,
    freeSearchesPerDay: DL_INTEL_FREE_SEARCHES_PER_DAY,
    freeSearchesLeft: intelEconomy.freeLeft,
    searchCost: DL_INTEL_SEARCH_COST,
    regionSearchCost: DL_INTEL_REGION_SEARCH_COST,
    earnedToday: intelEconomy.earnedToday,
    dailyEarnCap: 0, // 0 = SIN tope (migración 011) — el mock refleja el backend vivo
    region: regionId
      ? {
          regionId,
          cellsTotal: intelEconomy.cellsTotal,
          cellsScanned: intelEconomy.cellsScanned,
          scannedToday: intelEconomy.scannedToday
        }
      : null
  };
}

// Cobro simulado: gratis del día primero, después tokens; sin fondos → el mismo
// error que lanza el RPC real (la UI lo traduce a intelligence.errorTokens).
function mockChargeSearch(isRegionSearch: boolean): void {
  if (intelEconomy.freeLeft > 0) {
    intelEconomy.freeLeft -= 1;
    return;
  }
  const cost = dlSearchCost(mockIntelStatus(), isRegionSearch);
  if (intelEconomy.balance < cost) throw new Error(`insufficient tokens: balance ${intelEconomy.balance}, need ${cost}`);
  intelEconomy.balance -= cost;
}

// Semilla del chat de soporte (doc 14): una respuesta previa del soporte para
// poder maquetar burbujas de ambos lados y el badge de no-leídos sin backend.
const supportMessages: SupportMessage[] = [
  {
    id: 'mock-support-1',
    sender: 'user',
    body: 'Hola, el bot se queda parado al recolectar hierro.',
    createdAt: new Date(Date.now() - 26 * 3600_000).toISOString(),
    readAt: new Date(Date.now() - 25 * 3600_000).toISOString()
  },
  {
    id: 'mock-support-2',
    sender: 'support',
    body: 'Hola! ¿Puedes adjuntar una captura del emulador cuando ocurra? Así lo vemos al momento.',
    createdAt: new Date(Date.now() - 24 * 3600_000).toISOString(),
    readAt: null
  }
];

let bot: BotStatus = {
  running: false,
  state: 'idle',
  text: 'Sin actividad.',
  statusCode: 'idle',
  statusParams: {},
  desired: 'stop',
  modules: { collect: true, train_troops: false, auto_hunt: false },
  settings: defaultSettings(),
  lastReportAt: new Date().toISOString(),
  clientVersion: '1.0.0-mock',
  snapshots: {
    marches: [
      { queue: 1, status: 'gathering', remaining: '01:12:44' },
      { queue: 2, status: 'idle', remaining: null }
    ],
    training: [{ queue: 1, unit: 'infantry', status: 'training', remaining: '00:41:02' }]
  }
};

// Flota mock (0.6): dos bots para probar tarjetas, contador y eliminar.
const mockFleet: BotSummary[] = [
  {
    instanceId: 'mock-inst-1',
    instanceLabel: 'Cuenta principal',
    running: false,
    state: 'idle',
    statusCode: 'idle',
    statusParams: {},
    text: 'Sin actividad.',
    desired: 'stop',
    clientVersion: '0.6.0-mock'
  },
  {
    instanceId: 'mock-inst-2',
    instanceLabel: 'Granja hierro',
    running: true,
    state: 'running',
    statusCode: 'running',
    statusParams: { modules: 'collect' },
    text: 'Ejecutando: collect',
    desired: 'run',
    clientVersion: '0.6.0-mock'
  }
];

function entFromStorage(): Entitlement {
  const status = (localStorage.getItem('mock_entitlement') as Entitlement['status']) || 'active';
  return {
    status,
    plan: status === 'inactive' || status === 'canceled' ? null : 'downloadable',
    currentPeriodEnd: localStorage.getItem('mock_trial_end'),
    hasHostedAccount: false,
    hasRecord: status !== 'inactive',
    viaStripe: status === 'active'
  };
}

export const mockApi: Api = {
  mode: 'mock',
  async getSession() {
    return MOCK_USER;
  },
  async signInWithEmail() {},
  async signInWithGoogle() {},
  async signOut() {
    localStorage.removeItem('mock_entitlement');
  },
  async getEntitlement() {
    return entFromStorage();
  },
  async startCheckout() {
    localStorage.setItem('mock_entitlement', 'active');
    return { url: window.location.href }; // en mock, "pagar" activa y recarga
  },

  async startTrial() {
    const end = new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString();
    localStorage.setItem('mock_entitlement', 'trialing');
    localStorage.setItem('mock_trial_end', end);
    return { trialEnd: end };
  },
  async startBillingPortal() {
    return { url: window.location.href }; // en mock no hay Stripe
  },
  async getBotStatus() {
    return { ...bot, lastReportAt: new Date().toISOString() };
  },
  async setDesired(desired, modules) {
    const active = (Object.keys(modules) as ModuleKey[]).filter((k) => modules[k]);
    bot = {
      ...bot,
      desired,
      modules: { ...modules },
      running: desired === 'run',
      state: desired === 'run' ? 'running' : 'idle',
      text: desired === 'run' ? 'Ejecutando: ' + (active.join(', ') || 'sin módulos') : 'Detenido por el usuario.',
      statusCode: desired === 'run' ? 'running' : 'idle',
      statusParams: desired === 'run' ? { modules: active.join(',') } : {}
    };
  },

  async saveModules(modules) {
    bot = { ...bot, modules: { ...modules } };
  },

  async saveSettings(settings) {
    bot = { ...bot, settings: { ...settings } };
  },

  async requestCapture() {
    // en mock el "cliente" responde al instante: refresca capture_at
    bot = { ...bot, captureAt: new Date().toISOString() };
  },

  // --- flota (0.6): dos bots de mentira para desarrollar la vista de flota ---
  async listBots() {
    return mockFleet.map((summary) => ({ ...summary, lastReportAt: new Date().toISOString() }));
  },

  async deleteBot(instanceId: string) {
    const index = mockFleet.findIndex((b) => b.instanceId === instanceId);
    if (index >= 0) mockFleet.splice(index, 1);
  },

  // --- Intelligence (doc 13): en mock, toda búsqueda encuentra un resultado ---
  async listIntelligenceRequests() {
    return [...intelligenceRequests];
  },

  async submitIntelligenceRequest(playerName: string, regionId?: string) {
    const id = `mock-intel-${Date.now()}`;
    intelligenceRequests.unshift({
      id,
      playerName,
      regionId: regionId ?? '4347',
      status: 'completed',
      createdAt: new Date().toISOString(),
      results: [
        {
          playerName: `${playerName}King`,
          allianceTag: 'SEC',
          allianceName: 'SecondHome',
          power: 28_450_000,
          castleX: 512,
          castleY: 384,
          lastSeenAt: new Date(Date.now() - 3600_000).toISOString(),
          confidence: 0.97
        }
      ]
    });
    return id;
  },

  async submitIntelligenceRegionRequest(regionId: string) {
    // Región completa: en mock devuelve la lista entera de la "región".
    const id = `mock-intel-region-${Date.now()}`;
    intelligenceRequests.unshift({
      id,
      playerName: '',
      regionId: regionId || '4347',
      queryType: 'region',
      status: 'completed',
      createdAt: new Date().toISOString(),
      results: [...SEC_RESULTS]
    });
    return id;
  },

  async deleteIntelligenceRequest(requestId: string) {
    const row = intelligenceRequests.find((r) => r.id === requestId);
    if (row) row.hiddenAt = new Date().toISOString();
  },

  // --- Intelligence 0.6.4 (doc 17): mapeo + tokens, simulados en memoria ---
  async dlIntelligenceStatus(regionId?: string) {
    return mockIntelStatus(regionId?.trim() || undefined);
  },

  async dlSubmitIntelligenceRequest(playerName: string, regionId?: string) {
    mockChargeSearch(false);
    return this.submitIntelligenceRequest(playerName, regionId);
  },

  async dlSubmitRegionRequest(regionId: string) {
    mockChargeSearch(true);
    return this.submitIntelligenceRegionRequest(regionId);
  },

  async ensureRegionSeeded(regionId: string): Promise<RegionSeedResult> {
    // En mock: formato inválido → mismo error que el RPC; válida → "sembrada"
    // la primera vez y "ya en la red" después (idempotencia visible en la UI).
    const region = regionId.trim();
    if (!isValidMappingRegion(region)) throw new Error('invalid region id');
    const known = localStorage.getItem('mock_seeded_regions') ?? '';
    const seeded = !known.split(',').includes(region);
    if (seeded) localStorage.setItem('mock_seeded_regions', known ? `${known},${region}` : region);
    return { regionId: region, seeded, totalCells: intelEconomy.cellsTotal };
  },

  async dlShieldWatchHistory(limit = 20): Promise<ShieldWatchEvent[]> {
    // Historial de ejemplo: una caída con su sospecha previa y el escudo antes.
    const now = Date.now();
    const base = { x: 434, y: 508, regionId: '4346', targetName: 'Sila', instanceId: 'default' };
    const events: ShieldWatchEvent[] = [
      // Auto Shield (familia 'city'): alimenta el feed de Actividad y el badge.
      { state: 'protected', x: null, y: null, regionId: null, targetName: null,
        instanceId: 'default', domeP: null, at: new Date(now - 2 * 60000).toISOString(),
        notified: true, hasCapture: true, stamp: Math.floor((now - 2 * 60000) / 1000) },
      { ...base, state: 'down', domeP: 0.03, at: new Date(now - 4 * 60000).toISOString(),
        notified: true, hasCapture: true, stamp: Math.floor((now - 4 * 60000) / 1000) },
      { ...base, state: 'confirming', domeP: 0.02, at: new Date(now - 6 * 60000).toISOString(),
        notified: true, hasCapture: true, stamp: Math.floor((now - 6 * 60000) / 1000) },
      // El más viejo ya no conserva imagen: solo se guardan las 5 últimas.
      { ...base, state: 'shielded', domeP: 0.97, at: new Date(now - 40 * 60000).toISOString(),
        notified: false, hasCapture: false, stamp: Math.floor((now - 40 * 60000) / 1000) }
    ];
    return events.slice(0, limit);
  },

  async dlShieldWatchCapture(): Promise<string | null> {
    // Un PNG de 1x1 gris: en mock basta con que el visor se abra y pinte algo.
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';
  },

  // --- Soporte (doc 14): en mock, "soporte" contesta solo a los 2 segundos ---
  async listSupportMessages() {
    return supportMessages.map((m) => ({ ...m }));
  },

  async sendSupportMessage(body: string, attachment?: File | null) {
    supportMessages.push({
      id: `mock-support-${Date.now()}`,
      sender: 'user',
      body,
      attachmentUrl: attachment ? URL.createObjectURL(attachment) : null,
      createdAt: new Date().toISOString(),
      readAt: null
    });
    window.setTimeout(() => {
      supportMessages.push({
        id: `mock-support-reply-${Date.now()}`,
        sender: 'support',
        body: 'Recibido (respuesta simulada). En producción esto lo escribe el dueño desde Telegram.',
        createdAt: new Date().toISOString(),
        readAt: null
      });
    }, 2000);
  },

  async markSupportRead() {
    const now = new Date().toISOString();
    for (const m of supportMessages) {
      if (m.sender === 'support' && !m.readAt) m.readAt = now;
    }
  },

  async notifySignup() {
    /* en mock no hay Telegram */
  },

  async logClientDownload() {
    /* en mock no hay embudo que registrar */
  }
};
